
import React, { Component } from 'react';
import { type Dispatch } from 'redux';

import logger from '../../app/logger';
import { getPictureInPictureModeConfig, getToolbarButtons, setPictureInPictureModeConfig } from '../../base/config';
import { browser } from '../../base/lib-meet-hour';
import { MEDIA_TYPE } from '../../base/media';
import { PARTICIPANT_ROLE, getParticipantById, isLocalParticipantModerator } from '../../base/participants';
import { getTrackByMediaTypeAndParticipant } from '../../base/tracks';
import { E2EELabel } from '../../e2ee';
import { isFilmstripVisible } from '../../filmstrip';
import { LocalRecordingLabel } from '../../local-recording';
import { PictureInPictureLabel } from '../../picture-in-picture';
import { RecordingLabel } from '../../recording';
import { TranscribingLabel } from '../../transcribing';
import { shouldDisplayTileView } from '../../video-layout';
import { VideoQualityLabel } from '../../video-quality';
import { VoiceCommandLabel } from '../../voice-command';

/**
 * The toolbar buttons array the cached Set was built from, and the Set itself.
 */
let _visibleButtonsSource;
let _visibleButtonsSet;

/**
 * Returns a Set of the given toolbar buttons, reusing the previous one while the array is the same. A new Set
 * on every call failed react-redux's shallow comparison and re-rendered the stage labels on every action.
 *
 * @param {Array<string>} buttons - The toolbar buttons.
 * @returns {Set<string>}
 */
function _getVisibleButtonsSet(buttons) {
    if (buttons !== _visibleButtonsSource || !_visibleButtonsSet) {
        _visibleButtonsSource = buttons;
        _visibleButtonsSet = new Set(buttons);
    }

    return _visibleButtonsSet;
}

/**
 * The type of the React {@code Component} props of {@link AbstractLabels}.
 */
export type Props = {

    /**
     * Whether the filmstrip is displayed with remote videos. Used to determine
     * display classes to set.
     */
    _filmstripVisible: boolean,

    /**
     * Whether the video quality label should be displayed.
     */
    _showVideoQualityLabel: boolean,

    _isVoiceCommandDisabled: boolean,
};

/**
 * A container to hold video status labels, including recording status and
 * current large video quality.
 *
 * @augments Component
 */
export default class AbstractLabels<P: Props, S> extends Component<P, S> {
    /**
   * Constructor for AbstractLabels
   *
   *
   * @augments Component
   */
    constructor(props) {
        super(props);

        // Access the translation function from props
        this.t = this.props.t;

        // ... your other constructor logic ...
    }

    /**
   * Renders the {@code E2EELabel}.
   *
   * @protected
   * @returns {React$Element}
   */
    _renderE2EELabel() {
        return <E2EELabel />;
    }

    /**
   * Renders the {@code LocalRecordingLabel}.
   *
   * @protected
   * @returns {React$Element}
   */
    _renderLocalRecordingLabel() {
        return <LocalRecordingLabel />;
    }

    /**
   * Renders the {@code RecordingLabel} that is platform independent.
   *
   * @param {string} mode - The recording mode that this label is rendered
   * for.
   * @protected
   * @returns {React$Element}
   */
    _renderRecordingLabel(mode: string) {
        return <RecordingLabel mode = { mode } />;
    }

    /**
   * Renders the {@code TranscribingLabel}.
   *
   * @protected
   * @returns {React$Element}
   */
    _renderTranscribingLabel() {
        return <TranscribingLabel />;
    }

    /**
   * Renders the {@code InsecureRoomNameLabel}.
   *
   * @protected
   * @returns {React$Element}
   */
    _renderInsecureRoomNameLabel() {
        // This method should be overridden by concrete implementations
        // to avoid circular dependency issues
        return null;
    }

    /**
   * Renders the {@code VideoQualityLabel} that is platform independent.
   *
   * @protected
   * @returns {React$Element}
   */
    _renderVideoQualityLabel() {
        return <VideoQualityLabel />;
    }

    /**
   * Renders the {@code VoiceCommandLabel} that is platform independent.
   *
   * @protected
   * @returns {React$Element}
   */
    _renderVoiceCommandLabel() {
        if (this.props._isVoiceCommandDisabled) {
            return;
        }

        return <VoiceCommandLabel />;
    }

    /**
   * Renders the {@code VoiceCommandLabel} that is platform independent.
   *
   * @protected
   * @returns {React$Element}
   */
    _renderPictureInPictureLabel() {
        return <PictureInPictureLabel { ...this.props } />;
    }
}

/**
 * Maps (parts of) the redux state to the associated props of the {@link Labels}
 * {@code Component}.
 *
 * @param {Object} state - The redux state.
 * @private
 * @returns {{
 *     _filmstripVisible: boolean,
 *     _showVideoQualityLabel: boolean
 * }}
 */
export function _abstractMapStateToProps(state: Object) {
    const isPersonModerator = isLocalParticipantModerator(state);
    const { participantId } = state['features/large-video'];
    const videoTrackOnLargeVideo = getTrackByMediaTypeAndParticipant(
        state['features/base/tracks'],
        MEDIA_TYPE.SCREENSHARE,
        participantId
    ) || getTrackByMediaTypeAndParticipant(
        state['features/base/tracks'],
        MEDIA_TYPE.VIDEO,
        participantId
    );
    const isScreenSharing = videoTrackOnLargeVideo?.videoType === 'desktop' && !videoTrackOnLargeVideo?.muted;
    const _participants = state['features/base/participants'];
    const { fullScreen } = state['features/toolbox'];
    const conferenceView = state['features/base/conference'].conferenceView;
    const _participant = getParticipantById(state, participantId);
    const _objectFit = state['features/base/conference'].objectFit;
    const { getUrl } = state['features/donorbox'];
    const { clickAndPledgeUrl } = state['features/clickandpledge'];
    const { iAmRecorder, interfaceConfig } = state['features/base/config'];
    const renderModeratorIndicator
        = _participant?.role === PARTICIPANT_ROLE.MODERATOR;
    const screensharetrack = state['features/base/tracks'].find(it => it.videoType === 'desktop' && it.mediaType === MEDIA_TYPE.SCREENSHARE && !it.muted);
    const { hideHeaderFooter } = state['features/filmstrip'];

    const youtubeSharingParticipant = state['features/shared-video']?.participantSharingVideo;
    const whiteboardParticipantId = state['features/genericiframe'].partId;
    const etherpadParticipantId = state['features/etherpad'].partId;
    const screenSharePartId = screensharetrack?.participantId;
    const isShareAudioTrack = state['features/base/tracks'].find(it => it.mediaType === MEDIA_TYPE.AUDIO && it.participantId === participantId && it.mhTrack.track.label === 'Tab audio');
    const isSharingAudio = state['features/screen-share']?.isSharingAudio;


    // in future this will be based on jwt.
    const isVoiceCommandDisabled = Boolean(
        interfaceConfig?.DISABLE_VOICE_COMMAND || !isPersonModerator
    );
    const tileViewLimit = state['features/filmstrip'].tileViewTileLimit;
    const largevideofullscreen = state['features/large-video'].largevideofullscreen;

    const {
        labelsVisible
    } = state['features/toolbox'];

    return {
        _filmstripVisible: isFilmstripVisible(state),
        _showVideoQualityLabel: !shouldDisplayTileView(state),
        _isVoiceCommandDisabled: isVoiceCommandDisabled,
        _isPictureInPictureVisible: getPictureInPictureModeConfig(state),
        _videoTrack: videoTrackOnLargeVideo,
        _iAmRecorder: iAmRecorder,
        _filmStripView: state['features/base/conference'].filmStripView,
        _tileViewEnabled: shouldDisplayTileView(state),
        _participantsLength: _participants?.length,
        _fullScreen: fullScreen,
        participantId,
        renderModeratorIndicator,
        objectFit: _objectFit,
        _isDominantSpeakerDisabled: interfaceConfig.DISABLE_DOMINANT_SPEAKER_INDICATOR,
        _conferenceView: conferenceView,
        _dominantSpeaker: _participant?.dominantSpeaker,
        _participant,
        _isModeratorPinned: _participant?.moderatorPin,
        isPersonModerator,
        _visibleButtons: _getVisibleButtonsSet(getToolbarButtons(state)),
        _geturl: getUrl,
        isParticipantScreenSharing: screenSharePartId === participantId,
        isParticipantSharingWhiteboard: whiteboardParticipantId === participantId,
        isParticipantSharingEtherpad: etherpadParticipantId === participantId,
        isSharingYoutubeVideo: youtubeSharingParticipant === participantId,
        clickAndPledgeUrl,
        _tileViewLimit: tileViewLimit,
        _connectionIndicatorDisabled: interfaceConfig.CONNECTION_INDICATOR_DISABLED,
        _connectionIndicatorAutoHideEnabled:
            interfaceConfig.CONNECTION_INDICATOR_AUTO_HIDE_ENABLED,
        _showtoolbox: labelsVisible,
        isParticipantSharingAudio: isSharingAudio && isShareAudioTrack !== undefined,
        _isSharing: isScreenSharing,
        hideHeaderFooter,
        largevideofullscreen,
        isParticipantScreenSharingTrack: _participant?.isFakeParticipant === 'LocalScreenShare' || _participant?.isFakeParticipant === 'RemoteScreenShare'

    };
}

/**
 * Maps dispatching of some action to React component props.
 *
 * @param {Function} dispatch - Redux action dispatcher.
 * @private
 * @returns {{
 *     _onUnmount: Function
 * }}
 */
export function _abstractMapDispatchToProps(dispatch: Dispatch<any>) {
    return {
        /**
         * Dispatches actions to store picture in picture mode config.
         *
         * @param {string} streamId - The ID of the stream.
         * @param {Transform} transform - The last applied transform.
         * @private
         * @returns {void}
         */
        onClickedLabel(value) {
            if (browser.isSafari()) {
                if (value) {
                    const videoElement = ((document.getElementById('largeVideo'): any): HTMLVideoElement)
                        || document.querySelector('video');

                    if (videoElement && typeof videoElement.requestPictureInPicture === 'function') {
                        videoElement.requestPictureInPicture()
                            .then(() => {
                                dispatch(setPictureInPictureModeConfig(true));
                            })
                            .catch(error => {
                                logger.warn('Failed to enter Picture-in-Picture mode in Safari:', error);
                                dispatch(setPictureInPictureModeConfig(false));
                            });
                    } else {
                        dispatch(setPictureInPictureModeConfig(value));
                    }
                } else if (document.pictureInPictureElement) {
                    document.exitPictureInPicture()
                            .then(() => {
                                dispatch(setPictureInPictureModeConfig(false));
                            })
                            .catch(error => {
                                logger.warn('Failed to exit Picture-in-Picture mode in Safari:', error);
                                dispatch(setPictureInPictureModeConfig(false));
                            });
                } else {
                    dispatch(setPictureInPictureModeConfig(false));
                }
            } else {
                dispatch(setPictureInPictureModeConfig(value));
            }
        }
    };
}
